import React from "react";
import { Bus, MapPin, ShieldCheck } from "lucide-react";
import { CampusLifeShell, InfoList, PageSection } from "./CampusLifePageParts";

const busRoutes = [
  {
    route: "A Hubli",
    stops: [
      ["Manjunath Nagar Cross", "6:40"],
      ["Akshay Park", "6:45"],
      ["Railway Station", "6:50"],
      ["Old Bus Stand", "6:55"],
      ["BVBCET", "7:00"],
      ["Navnagar", "7:10"],
      ["Shrinagar (Dharwad)", "7:30"],
    ],
  },
  {
    route: "B Dharwad",
    stops: [
      ["Gandhi Nagar", "7:15"],
      ["Bagalkot Petrol Pump", "7:20"],
      ["Emmikeri", "7:22"],
      ["Michigan Compound", "7:25"],
      ["Jayanagar", "7:27"],
      ["Shrinagar", "7:30"],
    ],
  },
  {
    route: "C",
    stops: [
      ["Tejaswi Nagar", "7:10"],
      ["Toll Naka", "7:15"],
      ["NTTF", "7:20"],
      ["Court Circle", "7:22"],
      ["Jubilee Circle", "7:25"],
      ["Shrinagar", "7:30"],
    ],
  },
  {
    route: "D",
    stops: [
      ["Murugmath", "7:10"],
      ["Depot Circle", "7:15"],
      ["Shivaji Circle", "7:18"],
      ["Maratha Colony", "7:20"],
      ["Old DSP Office", "7:22"],
      ["Dasankoppa", "7:27"],
      ["Shrinagar", "7:30"],
    ],
  },
  {
    route: "E",
    stops: [
      ["New Bus Stand", "7:05"],
      ["MG Bank", "7:10"],
      ["Police Headquarters", "7:13"],
      ["Sadhankeri", "7:18"],
      ["Sainagar", "7:23"],
      ["Dasankoppa", "7:28"],
      ["Shrinagar", "7:30"],
    ],
  },
  {
    route: "F",
    stops: [
      ["Galgali Hospital", "7:15"],
      ["Shivgiri", "7:18"],
      ["Kalyan Nagar", "7:22"],
      ["Karnataka University Circle", "7:27"],
      ["Shrinagar", "7:30"],
    ],
  },
  {
    route: "G",
    stops: [
      ["Navnagar", "7:05"],
      ["SDM Medical", "7:14"],
      ["Dental Cross", "7:16"],
      ["Navalur", "7:18"],
      ["Gandhi Nagar", "7:20"],
      ["Shrinagar", "7:30"],
    ],
  },
  {
    route: "H (Dandeli)",
    stops: [
      ["Dandeli Bus Stand", "7:20"],
      ["Somani Circle", "7:22"],
      ["Ganesh Nagar", "7:25"],
      ["VTU Ext Center", "7:30"],
      ["BNJC", "7:33"],
      ["Bamboo Gate", "7:35"],
      ["Third Gate", "7:40"],
    ],
  },
  {
    route: "I (Belgaum)",
    stops: [
      ["KLS Society", "6:20"],
      ["Third Gate", "6:30"],
      ["Khanapur", "7:05"],
      ["Beedi", "7:30"],
      ["Kakkeri", "7:50"],
      ["Alnavar", "8:00"],
    ],
  },
];

const maxStopsPerRoute = Math.max(...busRoutes.map((route) => route.stops.length));

const Transportation = () => (
  <CampusLifeShell
    title="Transportation"
    short="Transport"
    image="/Campus/Transporthero.jpeg"
    navItems={[
      ["#overview", "Overview"],
      ["#coverage", "Bus Routes and Coverage"],
      ["#commute", "Student Commute Support"],
      ["#safety", "Safety and Convenience"],
    ]}
    quickFacts={[
      ["Category", "Campus Life"],
      ["Facility", "Transportation"],
      ["Coverage", "Dandeli and Belagavi and near places"],
      // ["Routes", "To be updated"],
      // ["Support", "Day scholar commute"],
      // ["Schedule", "Regular academic schedules"],
      // ["Contact", "To be updated"],
    ]}
  >
    <PageSection id="overview" title="Transportation" icon={Bus}>
      <span className="text-[11px] sm:text-xs tracking-[0.16em] sm:tracking-[0.2em] text-brand font-sans-ui font-semibold">
        Campus Life
      </span>
      <p className="text-[#2a2a2a]/85 leading-relaxed text-[15px] sm:text-[15.5px] mt-3">
        The college transport facility supports day scholars with convenient
        connectivity between the campus, Haliyal, Dandeli and surrounding areas.
      </p>

      <div className="grid gap-4 sm:grid-cols-2 mt-5">
        {[
          ["/Campus/transport.jpeg", "KLS VDIT transportation facility"],
          ["/Campus/transport2.jpg", "KLS VDIT campus transport bus"],
        ].map(([src, alt]) => (
          <div key={src} className="bg-white border border-brand/15 overflow-hidden">
            <img
              src={src}
              alt={alt}
              className="w-full aspect-[4/3] object-cover"
              loading="lazy"
            />
          </div>
        ))}
      </div>
    </PageSection>

    <PageSection id="coverage" title="Bus Routes and Coverage" icon={MapPin}>
      <InfoList
        items={[
          "College buses connect the campus with Haliyal, Dandeli and nearby areas.",
          "Stop-wise boarding timings are listed below in A.M.",
        ]}
      />
      <div className="mt-5 space-y-4 md:hidden">
        {busRoutes.map((route) => (
          <div key={`mobile-${route.route}`} className="overflow-x-auto bg-white">
            <table className="min-w-[280px] w-full table-fixed border-collapse bg-white text-center text-[11px] leading-tight text-black">
              <thead>
                <tr>
                  <th
                    colSpan={2}
                    className="border border-black bg-[#f7f7f7] px-2 py-1.5 font-bold"
                  >
                    Route {route.route}
                  </th>
                </tr>
                <tr>
                  <th className="border border-black px-2 py-1.5 font-bold">
                    Boarding Point
                  </th>
                  <th className="w-16 border border-black px-2 py-1.5 font-bold">
                    Time
                  </th>
                </tr>
              </thead>
              <tbody>
                {route.stops.map(([stop, time]) => (
                  <tr key={`${route.route}-mobile-${stop}`}>
                    <td className="border border-black px-2 py-1.5 font-bold break-words">
                      {stop}
                    </td>
                    <td className="border border-black px-2 py-1.5 font-bold">
                      {time}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))}
      </div>
      <div className="mt-5 hidden overflow-x-auto bg-white md:block">
        <table className="min-w-[760px] w-full table-fixed border-collapse text-center text-[8px] leading-tight text-black sm:text-[9px] md:text-[10px] lg:text-[11px] xl:text-[12px]">
          <thead>
            <tr>
              <th className="w-[11%] border border-black px-1 py-1 font-bold">
                Route
              </th>
              <th
                colSpan={maxStopsPerRoute}
                className="border border-black px-1 py-1 font-bold"
              >
                Boarding Points (Bus Stops){" "}
                <span className="italic font-semibold">[Timings in A. M.]</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {busRoutes.map((route, routeIndex) => {
              const paddedStops = [
                ...route.stops,
                ...Array.from(
                  { length: maxStopsPerRoute - route.stops.length },
                  () => ["", ""]
                ),
              ];

              return (
                <React.Fragment key={route.route}>
                  {routeIndex > 0 && (
                    <tr>
                      <td
                        colSpan={maxStopsPerRoute + 1}
                        className="h-3 border-x border-black bg-[#8d8d8d] p-0"
                      />
                    </tr>
                  )}
                  <tr>
                    <td
                      rowSpan={2}
                      className="border border-black px-1 py-2 align-middle font-bold leading-tight break-words"
                    >
                      {route.route}
                    </td>
                    {paddedStops.map(([stop], index) => (
                      <td
                        key={`${route.route}-stop-${index}`}
                        className="border border-black px-1 py-2 align-middle font-bold leading-tight break-words"
                      >
                        {stop}
                      </td>
                    ))}
                  </tr>
                  <tr>
                    {paddedStops.map(([stop, time], index) => (
                      <td
                        key={`${route.route}-time-${index}`}
                        className="border border-black px-1 py-1.5 align-middle font-bold leading-tight break-words"
                      >
                        {time}
                      </td>
                    ))}
                  </tr>
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </PageSection>

    <PageSection id="commute" title="Student Commute Support" icon={Bus}>
      <InfoList
        items={[
          "The facility helps students commute comfortably and reliably.",
          "Transport support is planned around regular academic schedules.",
        ]}
      />
    </PageSection>

    <PageSection id="safety" title="Safety and Convenience" icon={ShieldCheck}>
      <InfoList
        items={[
          "Transport operations prioritize student convenience and timely commute.",
          "Transport contact details will be updated with official information.",
        ]}
      />
    </PageSection>
  </CampusLifeShell>
);

export default Transportation;
